import React, { useState } from 'react';
import { FiCornerUpLeft, FiEye, FiEyeOff, FiTrash2 } from 'react-icons/fi';
import type { ChannelCommentResponse } from '@troia-v3/types';

import {
  useChannelCommentsList,
  useChannelCommentsSocket,
  useDeleteComment,
  useHideComment,
  useReplyComment,
} from '../../hooks';
import { relativeSince } from './formatMetrics';
import { Button } from '@/shared/components/ui/Button';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

type StatusFilter = 'all' | 'visible' | 'hidden';
const FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'visible', label: 'Visíveis' },
  { key: 'hidden', label: 'Ocultos' },
];

interface RowProps {
  channelId: string;
  comment: ChannelCommentResponse;
}

const CommentRow: React.FC<RowProps> = ({ channelId, comment }) => {
  const confirm = useConfirm();
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const replyMutation = useReplyComment(channelId);
  const hideMutation = useHideComment(channelId);
  const deleteMutation = useDeleteComment(channelId);

  const initials = (comment.author.username || '??').slice(0, 2).toUpperCase();
  const deleted = comment.status === 'deleted';
  const hidden = comment.status === 'hidden';
  const busy = replyMutation.isPending || hideMutation.isPending || deleteMutation.isPending;

  const submitReply = (): void => {
    if (!replyText.trim() || replyMutation.isPending) return; // evita duplo-submit (Enter repetido) — reply não é idempotente
    replyMutation.mutate(
      { commentId: comment.id, message: replyText.trim() },
      { onSuccess: () => { setReplyText(''); setReplyOpen(false); } },
    );
  };

  return (
    <div className="cdash-comment" data-hidden={hidden || deleted} data-testid={`cdash-comment-${comment.id}`}>
      <div className="cdash-comment-av">
        {comment.author.profilePicUrl ? <img src={comment.author.profilePicUrl} alt={comment.author.username} /> : <span>{initials}</span>}
      </div>
      <div className="cdash-comment-body">
        <div className="cdash-comment-head">
          <span className="cdash-comment-user">@{comment.author.username || 'usuário'}</span>
          {comment.isFromMe ? <span className="cdash-comment-me">Você</span> : null}
          <span className="cdash-comment-time">{relativeSince(comment.occurredAt)}</span>
          {hidden ? <span className="cdash-comment-badge hidden">Oculto</span> : null}
          {deleted ? <span className="cdash-comment-badge deleted">Excluído</span> : null}
        </div>
        <div className="cdash-comment-text">{comment.text}</div>
        {!deleted && (
          <div className="cdash-comment-actions">
            {!comment.isFromMe && (
              <button className="cdash-comment-btn" onClick={() => setReplyOpen((v) => !v)} disabled={busy} data-testid={`cdash-comment-reply-${comment.id}`}>
                <FiCornerUpLeft size={13} /> Responder
              </button>
            )}
            <button
              className="cdash-comment-btn"
              onClick={() => hideMutation.mutate({ commentId: comment.id, hidden: !hidden })}
              disabled={busy}
              data-testid={`cdash-comment-hide-${comment.id}`}
            >
              {hidden ? <><FiEye size={13} /> Reexibir</> : <><FiEyeOff size={13} /> Ocultar</>}
            </button>
            <button
              className="cdash-comment-btn danger"
              onClick={async () => { if (await confirm({ message: 'Excluir este comentário no Instagram? Não pode ser desfeito.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) deleteMutation.mutate(comment.id); }}
              disabled={busy}
              data-testid={`cdash-comment-delete-${comment.id}`}
            >
              <FiTrash2 size={13} /> Excluir
            </button>
          </div>
        )}
        {replyOpen && (
          <div className="cdash-reply-box">
            <input
              className="cdash-reply-input"
              placeholder="Escreva uma resposta…"
              value={replyText}
              maxLength={2200}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitReply()}
              data-testid={`cdash-reply-input-${comment.id}`}
            />
            <Button variant="primary" size="md" onClick={submitReply} disabled={replyMutation.isPending || !replyText.trim()} data-testid={`cdash-reply-send-${comment.id}`}>
              Enviar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const CommentsSection: React.FC<{ channelId: string }> = ({ channelId }) => {
  const [filter, setFilter] = useState<StatusFilter>('all');
  useChannelCommentsSocket(channelId);
  const { data, isLoading } = useChannelCommentsList(channelId, { ...(filter !== 'all' ? { status: filter } : {}), limit: 50 });
  const comments = data?.items ?? [];

  return (
    <div data-testid="cdash-comments-section">
      <div className="cdash-comment-filter" data-testid="cdash-comment-filter">
        {FILTERS.map((f) => (
          <button key={f.key} className="cdash-chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)} data-testid={`cdash-comment-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>
      {isLoading ? (
        <div className="cdash-empty"><div className="cdash-empty-t">Carregando comentários…</div></div>
      ) : comments.length === 0 ? (
        <div className="cdash-empty" data-testid="cdash-comments-empty">
          <div className="cdash-empty-t">Nenhum comentário</div>
          <div className="cdash-empty-d">Comentários dos seus posts aparecem aqui após o sync ou em tempo real.</div>
        </div>
      ) : (
        <div>
          {comments.map((c) => (
            <CommentRow key={c.id} channelId={channelId} comment={c} />
          ))}
        </div>
      )}
    </div>
  );
};
